/**
 * Route Schedule Section JavaScript
 * Displays full-line schedule with 3 docks (departure, transfer, arrival)
 * Supports direction toggle: FROM primary dock / TO primary dock
 */

(function() {
  'use strict';

  // State
  let scheduleData = null;
  let currentDirection = 'forward'; // 'forward' = FROM primary dock, 'reverse' = TO primary dock
  let displayedDays = [];
  let maxDaysAhead = 5;
  let allSlots = [];
  let displayedSlotsCount = 0;
  let hasTransfer = true;
  let colCount = 3;
  const INITIAL_SLOTS_COUNT = 10;
  const LOAD_MORE_SLOTS_COUNT = 10;

  // Day mapping
  const dayMap = { 0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat' };

  /**
   * Check if a slot is active on a given day.
   * Priority 1: boolean fields (days_booleans) — if any is true, they are authoritative.
   * Priority 2: text field (days_available / days) — fallback for legacy slots.
   * If booleans and text disagree, booleans win.
   */
  function isSlotActiveOnDay(slot, dayAbbr) {
    if (slot.days_booleans) {
      var anyTrue = Object.values(slot.days_booleans).some(function(v) { return v === true; });
      if (anyTrue) {
        return slot.days_booleans[dayAbbr] === true;
      }
    }
    var text = slot.days_available || slot.days || '';
    return text.toLowerCase().includes(dayAbbr);
  }

  // DOM Elements
  let tableHeaderDocks;
  let tableHeaderLabels;
  let tableBody;
  let dirFromBtn;
  let dirToBtn;
  let dirDestination;
  let loadMoreBtn;
  let directionToggle;

  /**
   * Initialize the schedule section
   */
  function init() {
    // Get DOM elements
    tableHeaderDocks = document.getElementById('rs-table-header-docks');
    tableHeaderLabels = document.getElementById('rs-table-header-labels');
    tableBody = document.getElementById('rs-schedule-body');
    dirFromBtn = document.getElementById('rs-dir-from');
    dirToBtn = document.getElementById('rs-dir-to');
    dirDestination = document.getElementById('rs-dir-destination');
    loadMoreBtn = document.getElementById('rs-load-more');
    directionToggle = document.querySelector('.rs-direction-toggle');

    // Parse schedule data
    const dataEl = document.getElementById('schedule-data');
    if (!dataEl) {
      console.error('Route Schedule: No schedule data found');
      return;
    }

    try {
      scheduleData = JSON.parse(dataEl.textContent);
      const routes = Array.isArray(scheduleData.routes) ? scheduleData.routes : [];
      scheduleData.routesByHandle = routes.reduce(function(acc, route) {
        if (route && route.handle) {
          acc[route.handle] = route;
        }
        return acc;
      }, {});
    } catch (e) {
      console.error('Route Schedule: Failed to parse schedule data', e);
      return;
    }

    // Setup event listeners
    if (dirFromBtn) {
      dirFromBtn.addEventListener('click', function() {
        setDirection('forward');
      });
    }

    if (dirToBtn) {
      dirToBtn.addEventListener('click', function() {
        setDirection('reverse');
      });
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', loadMore);
    }

    // Initial render
    renderSchedule();
  }

  /**
   * Get the current route based on direction (used for reverse route check)
   */
  function getRoute() {
    const handle = currentDirection === 'forward'
      ? scheduleData.route_handle_forward
      : scheduleData.route_handle_reverse;
    if (!handle) return null;
    return scheduleData.routesByHandle[handle] || null;
  }

  /**
   * Get the anchor dock pair from the configured route handle.
   * This determines which dock pair to filter all routes by.
   */
  function getAnchorDockPair() {
    const handle = currentDirection === 'forward'
      ? scheduleData.route_handle_forward
      : scheduleData.route_handle_reverse;
    if (!handle) return null;

    const anchorRoute = scheduleData.routesByHandle[handle];
    if (!anchorRoute) return null;

    return {
      departure_dock: anchorRoute.departure_dock,
      arrival_dock: anchorRoute.arrival_dock
    };
  }

  /**
   * Get the anchor route for header rendering (dock names).
   */
  function getAnchorRoute() {
    const handle = currentDirection === 'forward'
      ? scheduleData.route_handle_forward
      : scheduleData.route_handle_reverse;
    if (!handle) return null;
    return scheduleData.routesByHandle[handle] || null;
  }

  /**
   * Get the transfer dock name from anchor route slots.
   */
  function getAnchorTransferDock() {
    var anchorRoute = getAnchorRoute();
    if (!anchorRoute || !anchorRoute.time_slots) return null;
    for (var i = 0; i < anchorRoute.time_slots.length; i++) {
      var td = anchorRoute.time_slots[i].transfer_dock;
      if (td && td !== '') return td;
    }
    return null;
  }

  /**
   * Build a column mapping for a sub-route relative to the anchor route's docks.
   * Returns an object describing which anchor column each sub-route dock maps to,
   * or null if the sub-route handle is not found or docks don't match.
   */
  function buildColumnMapping(subRouteConfig) {
    var handle = currentDirection === 'forward'
      ? subRouteConfig.route_handle_forward
      : subRouteConfig.route_handle_reverse;
    if (!handle) return null;

    var subRoute = scheduleData.routesByHandle[handle];
    if (!subRoute) return null;

    var anchorRoute = getAnchorRoute();
    if (!anchorRoute) return null;

    // Build ordered array of anchor dock names: [departure, transfer?, arrival]
    var anchorDocks = [anchorRoute.departure_dock];
    var transferDock = getAnchorTransferDock();
    if (transferDock) anchorDocks.push(transferDock);
    anchorDocks.push(anchorRoute.arrival_dock);

    // Map sub-route docks to anchor column indices
    var depCol = anchorDocks.indexOf(subRoute.departure_dock);
    var arrCol = anchorDocks.indexOf(subRoute.arrival_dock);

    // If neither dock matches any anchor dock, skip this sub-route
    if (depCol === -1 && arrCol === -1) return null;

    return {
      handle: handle,
      subRoute: subRoute,
      departureDockCol: depCol,
      arrivalDockCol: arrCol,
      anchorDocks: anchorDocks,
      missingDockText: subRouteConfig.missing_dock_text || 'Does Not Stop'
    };
  }

  /**
   * Get all routes matching a sub-route's dock pair for a specific date.
   * Applies the same date range filtering and override priority logic.
   */
  function getSubRouteMatchingRoutes(handle, date) {
    var subRoute = scheduleData.routesByHandle[handle];
    if (!subRoute) return [];

    var routes = Array.isArray(scheduleData.routes) ? scheduleData.routes : [];

    var matchingRoutes = routes.filter(function(route) {
      if (route.departure_dock !== subRoute.departure_dock) return false;
      if (route.arrival_dock !== subRoute.arrival_dock) return false;
      if (!isDateInRange(route, date)) return false;
      return true;
    });

    var overrideRoutes = matchingRoutes.filter(function(route) {
      return route.override === true && route.schedule_type === 'seasonal';
    });
    if (overrideRoutes.length > 0) matchingRoutes = overrideRoutes;

    return matchingRoutes;
  }

  /**
   * Get all routes matching the anchor dock pair for a specific date.
   * Applies date range filtering and override priority logic.
   */
  function getMatchingRoutes(date) {
    const anchorDockPair = getAnchorDockPair();
    if (!anchorDockPair) return [];

    const routes = Array.isArray(scheduleData.routes) ? scheduleData.routes : [];

    // Filter routes by dock pair and date range
    let matchingRoutes = routes.filter(function(route) {
      if (route.departure_dock !== anchorDockPair.departure_dock) return false;
      if (route.arrival_dock !== anchorDockPair.arrival_dock) return false;
      if (!isDateInRange(route, date)) return false;
      return true;
    });

    // Apply override priority: seasonal override routes take precedence
    const overrideRoutes = matchingRoutes.filter(function(route) {
      return route.override === true && route.schedule_type === 'seasonal';
    });

    if (overrideRoutes.length > 0) {
      matchingRoutes = overrideRoutes;
    }

    return matchingRoutes;
  }

  /**
   * Generate a unique key for a slot to prevent duplicates.
   * Includes transfer_arrival to distinguish slots with different transfer timings.
   */
  function getSlotKey(slot) {
    return [
      slot.departure_time || '',
      slot.arrival_time || '',
      slot.transfer_arrival || ''
    ].join('|');
  }

  /**
   * Convert AM/PM to lowercase in time string
   */
  function formatTimeLowercase(timeStr) {
    if (!timeStr) return '-';
    return timeStr.replace(/AM/g, 'am').replace(/PM/g, 'pm');
  }

  /**
   * Parse time string to minutes since midnight
   */
  function parseTime(timeStr) {
    if (!timeStr) return 0;
    const parts = timeStr.trim().split(' ');
    if (parts.length < 2) return 0;

    const [time, period] = parts;
    let [hours, minutes] = time.split(':').map(Number);

    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  /**
   * Normalize a user-entered time string to minutes since midnight.
   * Returns null if the string is unparseable — callers treat null as "do not dedupe".
   * Handles: varied spacing, casing, missing leading zeros, single-digit minutes.
   * Does NOT guess AM/PM — if missing, returns null (safe fallback).
   */
  function normalizeTime(str) {
    if (!str || typeof str !== 'string') return null;

    // Trim and collapse whitespace
    var s = str.trim().replace(/\s+/g, ' ').toLowerCase();

    // Extract am/pm suffix
    var period = null;
    var match = s.match(/\s*(am|pm)\s*$/);
    if (match) {
      period = match[1];
      s = s.slice(0, match.index).trim();
    } else {
      // No AM/PM found — refuse to guess
      return null;
    }

    // Must have a colon
    var colonIdx = s.indexOf(':');
    if (colonIdx === -1) return null;

    var hourStr = s.slice(0, colonIdx);
    var minStr = s.slice(colonIdx + 1);

    // Pad single-digit minutes (e.g. "5" → "05")
    if (minStr.length === 1) {
      minStr = '0' + minStr;
    }

    var hours = parseInt(hourStr, 10);
    var minutes = parseInt(minStr, 10);

    if (isNaN(hours) || isNaN(minutes)) return null;
    if (hours < 1 || hours > 12) return null;
    if (minutes < 0 || minutes > 59) return null;

    // Convert to 24-hour
    if (period === 'pm' && hours !== 12) hours += 12;
    if (period === 'am' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  /**
   * Check if a time has already passed today
   */
  function isTimePassed(timeStr) {
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return parseTime(timeStr) < nowMinutes;
  }

  /**
   * Check if a date is today
   */
  function isToday(date) {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  }

  /**
   * Check if date is within route's date range
   */
  function isDateInRange(route, date) {
    if (!route.start_date || !route.end_date) return true;

    const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const startDate = new Date(route.start_date + 'T00:00:00');
    const endDate = new Date(route.end_date + 'T23:59:59');

    return checkDate >= startDate && checkDate <= endDate;
  }

  /**
   * Get slots for a specific date from multiple routes.
   * Merges slots from all matching routes, deduplicates, and preserves all transfer fields.
   */
  function getSlotsForDate(routes, date) {
    if (!Array.isArray(routes) || routes.length === 0) return [];

    const dayOfWeek = dayMap[date.getDay()];
    const checkToday = isToday(date);
    const seenKeys = {};
    let allSlots = [];

    // Collect slots from all routes
    routes.forEach(function(route) {
      if (!route || !route.time_slots) return;
      if (!isDateInRange(route, date)) return;

      route.time_slots.forEach(function(slot) {
        // Check if this day is available (booleans primary, text fallback)
        if (!isSlotActiveOnDay(slot, dayOfWeek)) return;

        // If today, filter out passed times
        if (checkToday && isTimePassed(slot.departure_time)) {
          return;
        }

        // Deduplicate by composite key (preserves slots with different transfer timings)
        const key = getSlotKey(slot);
        if (seenKeys[key]) return;
        seenKeys[key] = true;

        // Push the full slot with all transfer fields preserved
        allSlots.push(slot);
      });
    });

    // Sort by departure time
    allSlots.sort(function(a, b) {
      return parseTime(a.departure_time) - parseTime(b.departure_time);
    });

    return allSlots;
  }

  /**
   * Format date for display
   */
  function formatDate(date, includeToday) {
    const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    const formatted = date.toLocaleDateString('en-US', options);

    if (includeToday && isToday(date)) {
      return 'Today - ' + formatted;
    }

    return formatted;
  }

  /**
   * Set the direction and re-render
   */
  function setDirection(direction) {
    currentDirection = direction;

    // Update button states
    if (dirFromBtn && dirToBtn) {
      dirFromBtn.classList.toggle('active', direction === 'forward');
      dirToBtn.classList.toggle('active', direction === 'reverse');
    }

    // Re-render
    renderSchedule();
  }

  /**
   * Gather all slots from today onwards using dock-pair filtering.
   * Automatically finds all routes matching the anchor dock pair for each date.
   */
  function gatherAllSlots() {
    allSlots = [];

    // Check if we have an anchor dock pair
    const anchorDockPair = getAnchorDockPair();
    if (!anchorDockPair) return allSlots;

    // Pre-compute sub-route column mappings (once per render)
    var subRoutes = scheduleData.sub_routes || [];
    var subRouteMappings = [];
    subRoutes.forEach(function(sr) {
      var mapping = buildColumnMapping(sr);
      if (mapping) subRouteMappings.push(mapping);
    });

    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + maxDaysAhead);

    let checkDate = new Date(today);
    while (checkDate <= maxDate) {
      // Get all matching routes for this specific date
      const matchingRoutes = getMatchingRoutes(checkDate);

      // Get merged slots from all matching routes
      const daySlots = getSlotsForDate(matchingRoutes, checkDate);

      daySlots.forEach(function(slot) {
        allSlots.push({
          date: new Date(checkDate),
          slot: slot,
          _subRouteMapping: null
        });
      });

      // Sub-route slots
      subRouteMappings.forEach(function(mapping) {
        var subMatchingRoutes = getSubRouteMatchingRoutes(mapping.handle, checkDate);
        var subDaySlots = getSlotsForDate(subMatchingRoutes, checkDate);
        subDaySlots.forEach(function(slot) {
          allSlots.push({
            date: new Date(checkDate),
            slot: slot,
            _subRouteMapping: mapping
          });
        });
      });

      checkDate.setDate(checkDate.getDate() + 1);
    }

    // Sort all slots: date first, then departure_time
    allSlots.sort(function(a, b) {
      var dateDiff = a.date.getTime() - b.date.getTime();
      if (dateDiff !== 0) return dateDiff;
      return parseTime(a.slot.departure_time) - parseTime(b.slot.departure_time);
    });

    // Remove sub-route slots that duplicate an anchor slot
    allSlots = deduplicateSubRouteSlots(allSlots);

    return allSlots;
  }

  /**
   * Remove sub-route slots that are duplicates of anchor slots.
   * A sub-route slot is a duplicate when its departure_time AND its arrival_time
   * (which maps to the anchor's transfer_departure column) both match an anchor
   * slot on the same date. If any time is unparseable, the slot is kept (safe).
   */
  function deduplicateSubRouteSlots(slots) {
    if (!scheduleData.hide_duplicate_subroute_slots) return slots;

    // Extract only digits and colon from a time string (e.g. "10:00 am" → "10:00")
    function extractDigits(str) {
      if (!str) return '';
      return str.replace(/[^0-9:]/g, '');
    }

    function norm(str) {
      var n = normalizeTime(str);
      return n !== null ? n : extractDigits(str);
    }

    // Collect anchor items per date
    var anchorsByDate = {};
    slots.forEach(function(item) {
      if (item._subRouteMapping !== null) return;
      var ds = item.date.toDateString();
      if (!anchorsByDate[ds]) anchorsByDate[ds] = [];
      anchorsByDate[ds].push(item);
    });

    // Build lookup: date+departureTime → anchor item (forward-style dedup)
    var anchorIndex = {};
    slots.forEach(function(item) {
      if (item._subRouteMapping !== null) return;
      var key = item.date.toDateString() + '|' + norm(item.slot.departure_time);
      anchorIndex[key] = item;
    });

    return slots.filter(function(item) {
      if (item._subRouteMapping === null) return true;

      var ds = item.date.toDateString();
      var subDepNorm = norm(item.slot.departure_time);
      var subArrNorm = norm(item.slot.arrival_time);

      // Strategy 1 (forward): sub-route departure matches anchor departure
      var key = ds + '|' + subDepNorm;
      var anchorItem = anchorIndex[key];
      if (anchorItem) {
        var anchorTransferNorm = norm(anchorItem.slot.transfer_departure);
        if (anchorTransferNorm && subArrNorm && String(subArrNorm) === String(anchorTransferNorm)) {
          return false; // duplicate — drop
        }
      }

      // Strategy 2 (reverse): sub-route departure matches anchor's transfer_departure
      // and sub-route arrival matches anchor's arrival
      var anchors = anchorsByDate[ds] || [];
      for (var i = 0; i < anchors.length; i++) {
        var a = anchors[i];
        var aTransferNorm = norm(a.slot.transfer_departure);
        if (aTransferNorm && String(subDepNorm) === String(aTransferNorm)) {
          var aArrNorm = norm(a.slot.arrival_time);
          if (aArrNorm && String(subArrNorm) === String(aArrNorm)) {
            return false; // duplicate — drop
          }
        }
      }

      return true;
    });
  }

  /**
   * Render the schedule table
   */
  function renderSchedule() {
    const hasReverseRoute = !!(
      scheduleData &&
      scheduleData.route_handle_reverse &&
      scheduleData.routesByHandle &&
      scheduleData.routesByHandle[scheduleData.route_handle_reverse]
    );

    if (!hasReverseRoute) {
      currentDirection = 'forward';
      if (directionToggle) directionToggle.style.display = 'none';
      if (dirFromBtn && dirToBtn) {
        dirFromBtn.classList.add('active');
        dirToBtn.classList.remove('active');
      }
    } else if (directionToggle) {
      directionToggle.style.display = '';
    }

    // Get anchor route for dock names in header
    const anchorRoute = getAnchorRoute();

    if (!anchorRoute) {
      colCount = 3;
      tableHeaderDocks.innerHTML = '';
      tableHeaderLabels.innerHTML = '';
      tableBody.innerHTML = '<tr><td colspan="' + colCount + '" class="rs-no-departures">No schedule data available.</td></tr>';
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
      return;
    }

    // Update destination label - keep primary dock fixed
    // The primary_dock is set in Liquid and doesn't change with direction
    // dirDestination is already set in HTML from Liquid

    // Reset state
    displayedDays = [];
    displayedSlotsCount = 0;

    // Clear table
    tableBody.innerHTML = '';

    // Gather all available slots using dock-pair filtering (no parameter needed)
    gatherAllSlots();

    // Determine if anchor route has a transfer dock (drives 2 vs 3 column mode)
    // Sub-routes do not force column mode changes — they map to existing columns
    hasTransfer = allSlots.some(function(item) {
      return item._subRouteMapping === null &&
             item.slot.transfer_dock && item.slot.transfer_dock !== '';
    });
    colCount = hasTransfer ? 3 : 2;

    const table = document.getElementById('rs-schedule-table');
    if (table) {
      table.classList.toggle('rs-cols-3', hasTransfer);
      table.classList.toggle('rs-cols-2', !hasTransfer);
    }

    if (allSlots.length === 0) {
      tableBody.innerHTML = '<tr><td colspan="' + colCount + '" class="rs-no-departures">No departures scheduled for the upcoming days.</td></tr>';
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
      return;
    }

    // Render header using anchor route dock names (decoupled from slot order)
    renderTableHeader(anchorRoute);

    // Render initial slots (first 10)
    renderNextSlots(INITIAL_SLOTS_COUNT);

    // Show/hide load more button
    updateLoadMoreButton();
  }

  /**
   * Render table header with dock names
   * Always shows: Departing (left) → Arriving (right) regardless of direction
   * The route data itself determines which docks are departure/arrival
   */
  function renderTableHeader(route) {
    const headerBgColor = scheduleData.header_bg_color || '#F5A623';

    // Get transfer dock name from anchor route slots (not from first displayed slot)
    const transferDock = getAnchorTransferDock() || 'Transfer';

    // Get dock names - these come from the selected route (forward or reverse)
    // so they're already correct for the chosen direction
    const departureDock = route.departure_dock || 'Departure';
    const arrivalDock = route.arrival_dock || 'Arrival';

    if (hasTransfer) {
      // 3 columns: Departure | Transfer | Arrival (always left to right)
      tableHeaderDocks.innerHTML =
        '<th style="background: ' + headerBgColor + ';">' + departureDock + '</th>' +
        '<th style="background: ' + headerBgColor + ';">' + (transferDock || 'Transfer') + '</th>' +
        '<th style="background: ' + headerBgColor + ';">' + arrivalDock + '</th>';

      tableHeaderLabels.innerHTML =
        '<th>Departing</th>' +
        '<th>Departing</th>' +
        '<th>Arriving</th>';
    } else {
      // 2 columns: Departure | Arrival (always left to right)
      tableHeaderDocks.innerHTML =
        '<th style="background: ' + headerBgColor + ';">' + departureDock + '</th>' +
        '<th style="background: ' + headerBgColor + ';">' + arrivalDock + '</th>';

      tableHeaderLabels.innerHTML =
        '<th>Departing</th>' +
        '<th>Arriving</th>';
    }
  }

  /**
   * Render a date separator row
   */
  function renderDateRow(date, isFirst) {
    const row = document.createElement('tr');
    row.className = 'rs-date-separator';
    row.innerHTML = '<td colspan="' + colCount + '">' + formatDate(date, isFirst) + '</td>';
    tableBody.appendChild(row);
  }

  /**
   * Render time slots (legacy function, kept for compatibility)
   * Always shows: departure_time (left) → arrival_time (right)
   */
  function renderSlots(slots) {
    slots.forEach(function(slot) {
      const row = document.createElement('tr');
      if (hasTransfer) {
        row.innerHTML =
          '<td>' + formatTimeLowercase(slot.departure_time) + '</td>' +
          '<td>' + formatTimeLowercase(slot.transfer_departure) + '</td>' +
          '<td>' + formatTimeLowercase(slot.arrival_time) + '</td>';
      } else {
        row.innerHTML =
          '<td>' + formatTimeLowercase(slot.departure_time) + '</td>' +
          '<td>' + formatTimeLowercase(slot.arrival_time) + '</td>';
      }
      tableBody.appendChild(row);
    });
  }

  /**
   * Render next N slots from allSlots array
   * Always shows: departure_time (left) → arrival_time (right)
   */
  function renderNextSlots(count) {
    const startIdx = displayedSlotsCount;
    const endIdx = Math.min(startIdx + count, allSlots.length);
    let lastDateStr = null;

    for (let i = startIdx; i < endIdx; i++) {
      const item = allSlots[i];
      const dateStr = item.date.toDateString();

      // Add date separator if new day
      if (dateStr !== lastDateStr) {
        const isFirst = displayedDays.length === 0;
        renderDateRow(item.date, isFirst);
        displayedDays.push(dateStr);
        lastDateStr = dateStr;
      }

      // Render the slot
      const row = document.createElement('tr');

      if (item._subRouteMapping) {
        // SUB-ROUTE SLOT — map departure/arrival to correct columns
        var m = item._subRouteMapping;
        if (hasTransfer) {
          // 3-column mode
          var cells = [];
          for (var c = 0; c < 3; c++) {
            if (c === m.departureDockCol) {
              cells.push(formatTimeLowercase(item.slot.departure_time));
            } else if (c === m.arrivalDockCol) {
              cells.push(formatTimeLowercase(item.slot.arrival_time));
            } else {
              cells.push('<span class="rs-does-not-stop">' + m.missingDockText + '</span>');
            }
          }
          row.innerHTML = '<td>' + cells[0] + '</td><td>' + cells[1] + '</td><td>' + cells[2] + '</td>';
        } else {
          // 2-column mode
          var col0 = m.departureDockCol === 0
            ? formatTimeLowercase(item.slot.departure_time)
            : (m.arrivalDockCol === 0 ? formatTimeLowercase(item.slot.arrival_time)
              : '<span class="rs-does-not-stop">' + m.missingDockText + '</span>');
          var col1 = m.arrivalDockCol === 1
            ? formatTimeLowercase(item.slot.arrival_time)
            : (m.departureDockCol === 1 ? formatTimeLowercase(item.slot.departure_time)
              : '<span class="rs-does-not-stop">' + m.missingDockText + '</span>');
          row.innerHTML = '<td>' + col0 + '</td><td>' + col1 + '</td>';
        }
      } else {
        // ANCHOR SLOT — existing logic unchanged
        if (hasTransfer) {
          var middleContent = formatTimeLowercase(item.slot.transfer_departure);
          if (item.slot.is_transfer === true) {
            middleContent += '<br><small class="rs-transfer-label">Transfer — change boats here</small>';
          }
          row.innerHTML =
            '<td>' + formatTimeLowercase(item.slot.departure_time) + '</td>' +
            '<td>' + middleContent + '</td>' +
            '<td>' + formatTimeLowercase(item.slot.arrival_time) + '</td>';
        } else {
          row.innerHTML =
            '<td>' + formatTimeLowercase(item.slot.departure_time) + '</td>' +
            '<td>' + formatTimeLowercase(item.slot.arrival_time) + '</td>';
        }
      }
      tableBody.appendChild(row);

      displayedSlotsCount++;
    }
  }

  /**
   * Load more slots
   */
  function loadMore() {
    if (displayedSlotsCount >= allSlots.length) {
      updateLoadMoreButton();
      return;
    }

    renderNextSlots(LOAD_MORE_SLOTS_COUNT);
    updateLoadMoreButton();
  }

  /**
   * Update load more button visibility
   */
  function updateLoadMoreButton() {
    if (!loadMoreBtn) return;

    const hasMore = displayedSlotsCount < allSlots.length;

    if (hasMore) {
      loadMoreBtn.style.display = 'inline-block';
      loadMoreBtn.disabled = false;
      loadMoreBtn.textContent = loadMoreBtn.getAttribute('data-text') || 'LOAD MORE';
    } else {
      if (allSlots.length > 0) {
        loadMoreBtn.style.display = 'inline-block';
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = 'No more departures scheduled';
      } else {
        loadMoreBtn.style.display = 'none';
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
